import React, { useState, useEffect } from 'react';
import { useLanguage } from './Header';
import { useTranslation } from '../utils/translations';

const Contact = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const [weatherData, setWeatherData] = useState({ main: { temp: 28, humidity: 65 } });
  const [forecast, setForecast] = useState([
    { weather: [{ main: 'Clear', description: 'sunny' }], main: { temp: 32, temp_min: 22 }, pop: 0 },
    { weather: [{ main: 'Clouds', description: 'partly cloudy' }], main: { temp: 29, temp_min: 20 }, pop: 0.1 },
    { weather: [{ main: 'Rain', description: 'light rain' }], main: { temp: 26, temp_min: 18 }, pop: 0.7 }
  ]);
  const [loading, setLoading] = useState(false);
  
  const API_KEY = 'a027b50ee06deba2c952e19c28f6d313';
  const lat = 12.9716;
  const lon = 77.5946;
  
  useEffect(() => {
    fetchWeatherData();
  }, []);
  
  const fetchWeatherData = async () => {
    try {
      // Current weather
      const currentResponse = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric`
      );
      
      if (!currentResponse.ok) {
        throw new Error(`HTTP error! status: ${currentResponse.status}`);
      }
      
      const currentData = await currentResponse.json();
      console.log('Current weather:', currentData);
      
      // 5-day forecast
      const forecastResponse = await fetch(
        `https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric`
      );
      
      if (!forecastResponse.ok) {
        throw new Error(`HTTP error! status: ${forecastResponse.status}`);
      }
      
      const forecastData = await forecastResponse.json();
      console.log('Forecast data:', forecastData);
      
      setWeatherData(currentData);
      if (forecastData.list) {
        setForecast(forecastData.list.slice(0, 7));
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching weather data:', error);
      setLoading(false);
      // Keep dummy data on error
    }
  };
  
  const getWeatherIcon = (weather) => {
    if (weather === 'Clear') return '☀️';
    if (weather === 'Clouds') return '☁️';
    if (weather === 'Rain') return '🌧️';
    if (weather === 'Thunderstorm') return '⛈️';
    if (weather === 'Snow') return '❄️';
    if (weather === 'Mist' || weather === 'Fog') return '🌫️';
    return '🌤️';
  };
  
  const getDayName = (index) => {
    const days = [t('today'), t('tomorrow'), t('wednesday'), t('thursday'), t('friday'), t('saturday'), t('sunday')];
    return days[index] || t('day');
  };
  
  const translateWeather = (weather) => {
    const weatherMap = {
      'Clear': t('sunny'),
      'Clouds': t('partlyCloudy'), 
      'Rain': t('lightRain'),
      'sunny': t('sunny'),
      'partly cloudy': t('partlyCloudy'),
      'light rain': t('lightRain')
    };
    return weatherMap[weather] || weather;
  };
  
  return (
    <section id="weather" className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('weatherSoilInsights')}</h2>
        
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">{t('weatherForecast7Day')}</h3>
            {loading ? (
              <div className="text-center py-8">
                <div className="text-2xl mb-2">🔄</div>
                <p className="text-gray-600">{t('loading')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {forecast.map((item, index) => {
                  if (!item.weather || !item.weather[0] || !item.main) return null;
                  return (
                    <div key={index} className={`flex items-center justify-between p-3 rounded-xl ${
                      item.weather[0].main === 'Rain' ? 'bg-blue-50' : 'bg-gray-50'
                    }`}>
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{getWeatherIcon(item.weather[0].main)}</span>
                        <div>
                          <p className="font-medium">{getDayName(index)}</p>
                          <p className="text-sm text-gray-600">{translateWeather(item.weather[0].description)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{Math.round(item.main.temp)}°/{Math.round(item.main.temp_min)}°</p>
                        <p className="text-sm text-gray-600">{item.pop ? Math.round(item.pop * 100) : 0}% {t('rain')}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">{t('soilConditions')}</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">{t('moistureLevel')}</span>
                <div className="flex items-center">
                  <div className="w-20 h-2 bg-gray-200 rounded-full mr-2">
                    <div className="w-3/4 h-2 bg-blue-500 rounded-full"></div>
                  </div>
                  <span className="font-medium">75%</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">{t('phLevel')}</span>
                <span className="font-medium text-green-600">6.8 ({t('good')})</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">{t('temperature')}</span>
                <span className="font-medium">{weatherData && weatherData.main ? Math.round(weatherData.main.temp) : '--'}°C</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">{t('nutrients')}</span>
                <span className="font-medium text-yellow-600">{t('medium')}</span>
              </div>
            </div>
            
            <div className="mt-6 p-3 bg-green-50 rounded-xl">
              <p className="text-sm text-green-700">
                ✅ {weatherData && weatherData.main ? `${t('humidity')}: ${weatherData.main.humidity}% - ${t('good')} ${t('forCrops')}` : t('soilOptimal')}
              </p>
            </div>
          </div>
          
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">{t('farmingAlerts')}</h3>
            <div className="space-y-3">
              <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <p className="font-medium text-yellow-800">{t('pestAlert')}</p>
                <p className="text-sm text-yellow-600">{t('pestAlertDesc')}</p>
              </div>
              
              <div className="p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                <p className="font-medium text-blue-800">{t('irrigationReminder')}</p>
                <p className="text-sm text-blue-600">{t('irrigationReminderDesc')}</p>
              </div>
              
              <div className="p-3 bg-green-50 border-l-4 border-green-400 rounded">
                <p className="font-medium text-green-800">{t('harvestReady')}</p>
                <p className="text-sm text-green-600">{t('harvestReadyDesc')}</p>
              </div>
            </div>
            
            <button className="btn-primary w-full mt-4">{t('viewAllAlerts')}</button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;